// src/utils/saveWhenConnected.js
// A save that waits for the signal instead of giving up (10/05/2026).
//
// Why: a checklist tick saves inside a Firestore transaction (utils/firebasefunctions.js
// mutateDealTasks, and utils/specialTasks.js for a special task), so it is decided against the
// checklist as saved right now. Firebase cannot run a transaction with no connection: it tries five
// times over about ten seconds, then gives up, and the screen puts the row back. Henry opens the
// MLS sites through a VPN, and each time the VPN switches on or off every open connection on the
// computer breaks for a while. A tick made just before he went to the MLS tab was quietly undone,
// and the "could not save" note had come and gone by the time he came back. A plain Firestore
// write (setDoc, updateDoc) is kept by Firebase and sent once the connection is back; this gives
// a transaction the same patience.
//
// How: the save is tried. When it fails for a connection reason (isConnectionError below), it is
// kept and tried again once the signal is usable: on the browser's "online" event, when the tab
// comes back on screen, and every CHECK_EVERY_MS, each time asking our own site first
// (utils/siteReachable.js), the app's one definition of a usable signal. Unlike a page download
// (utils/lazyPage.js), a save is tried again even while the tab is hidden: the browser still sends
// requests from a hidden tab, and the change should land while the person is away. A try that
// neither answers nor fails within ATTEMPT_TIMEOUT_MS counts as a connection failure too (Firebase
// can hang for good when a sign-in token could not be refreshed with no connection).
//
// Giving up: only once MAX_WAIT_MS has passed AND a last look at our site finds no answer (a laptop
// that slept through the half hour wakes with the timer already run out; its save is still tried).
// After the half hour a save gets one more try at most. It then fails the way it always did, marked
// so the screen says so in a note that stays until it is read (utils/toastWriteError.js).
//
// Meanwhile: the screens keep the change as the person left it, the navy bar at the bottom says a
// save is waiting (components/NoSignalBar.jsx reads the count here), and closing or leaving the
// page asks first (beforeunload), since a save that was never sent goes with the page. A try that is
// still out after COUNT_AFTER_MS is counted the same way, so a try that hangs is covered from then.
// The app's own reload after an upload waits for the count to reach zero (utils/lazyPage.js), and
// signing out forgets every waiting save (forgetWaitingSaves, App.js): it would only be refused.
//
// Any error that is not a connection error (a rule refusal, the owner's look-only view, a deal
// that is gone) is handed back at once: waiting would not help. The `attempt` callback must be
// safe to run again: each run decides afresh against what is saved (a transaction does), and a
// change already saved by an earlier run whose answer was lost must be recognised as done
// (utils/dealTaskActions.js and utils/specialTasks.js do, by their own stamps).
import { canReachSite } from "./siteReachable";

export const MAX_WAIT_MS = 30 * 60 * 1000;
export const CHECK_EVERY_MS = 15 * 1000;
// A healthy try answers in a second or two, and Firebase's own five tries over a dead connection
// take about ten seconds; a try still out after a minute is a request nothing is answering.
export const ATTEMPT_TIMEOUT_MS = 60 * 1000;
// A try still out after this is counted (the bar says "Saving your change..." and leaving asks).
export const COUNT_AFTER_MS = 2000;

export const WAITING_FOR_SIGNAL = "signal";
export const TRYING_NOW = "trying";

// ---- Connection errors ---------------------------------------------------------------------------

// Firestore's own codes for a request that could not get through or was answered by something that
// is not Firestore (a VPN gateway's error page comes back as "unknown"), Firebase Auth's code when a
// sign-in token could not be refreshed, and this file's own code for a try that never answered.
export const ATTEMPT_TIMED_OUT = "attempt-timed-out";
const CONNECTION_CODES = new Set([
  "unavailable",
  "deadline-exceeded",
  "unknown",
  "internal",
  "auth/network-request-failed",
  "network-request-failed",
  ATTEMPT_TIMED_OUT,
]);
// Firestore's codes for a definite answer: trying again later would get the same one.
const REFUSAL_CODES = new Set([
  "permission-denied",
  "unauthenticated",
  "not-found",
  "already-exists",
  "invalid-argument",
  "failed-precondition",
  "aborted",
  "out-of-range",
  "unimplemented",
  "data-loss",
  "resource-exhausted",
  "cancelled",
]);
const CONNECTION_WORDS =
  /client is offline|connection failed|could not be completed|request time ?out|server responded with status|network ?error|failed to fetch|load failed|network request failed|err_network|err_internet|internet connection/i;

export function browserIsOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function isConnectionError(err) {
  if (!err) return false;
  // The owner's look-only view refuses before anything is sent, and a save forgotten at sign-out is
  // over: never a connection problem.
  if (err.lookOnly === true || err.name === "LookOnlyError" || err.forgotten === true) return false;
  const code = String(err.code || "").toLowerCase().replace(/^firestore\//, "");
  if (REFUSAL_CODES.has(code)) return false;
  if (CONNECTION_CODES.has(code)) return true;
  if (CONNECTION_WORDS.test(String(err.message || ""))) return true;
  // Some other Firebase error while the browser itself says there is no connection. An error with no
  // code at all was thrown by the save's own decision ("Deal not found"): a definite answer.
  return code !== "" && browserIsOffline();
}

// Marks the error a save hands back once it stopped waiting, so a screen can say the wait ran out
// rather than "check your signal and try again" for a change that was made half an hour ago.
export function gaveUpWaiting(err) {
  return Boolean(err && err.gaveUpWaiting === true);
}

// Marks the error a save hands back when signing out forgot it.
export function forgottenAtSignOut(err) {
  return Boolean(err && err.forgotten === true);
}

function mark(err, field) {
  try {
    err[field] = true;
  } catch {
    // A frozen error object: the screen gets the plain failure message instead.
  }
  return err;
}

// ---- The shared count ------------------------------------------------------------------------------
//
// Every save counted right now (waiting for the signal, or being tried for a while), each with its
// phase, for the navy bar, the leave question and the app's own reload.

const kept = new Set();
let waitingForSignal = 0;
let tryingNow = 0;
const listeners = new Set();
// Goes up each time signing out forgets the saves, so a save can tell it was forgotten.
let forgetRound = 0;

function publishCounts() {
  let signal = 0;
  let trying = 0;
  kept.forEach((save) => {
    if (save.phase === WAITING_FOR_SIGNAL) signal += 1;
    else trying += 1;
  });
  if (signal === waitingForSignal && trying === tryingNow) return;
  waitingForSignal = signal;
  tryingNow = trying;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One listener's fault must never stop a save or keep the others from hearing.
    }
  });
}

// How many saves are counted right now, in either phase.
export function waitingSaveCount() {
  return waitingForSignal + tryingNow;
}

// "signal" while any counted save waits for a usable connection, otherwise "trying" while any is
// being tried, otherwise null. A plain string, so React's useSyncExternalStore can read it.
export function waitingSavePhase() {
  if (waitingForSignal > 0) return WAITING_FOR_SIGNAL;
  if (tryingNow > 0) return TRYING_NOW;
  return null;
}

// Shaped for React's useSyncExternalStore: the listener is called with nothing, and the returned
// function stops it.
export function subscribeWaitingSaves(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Calls `then` once no save is counted: at once when none is, otherwise when the last one lands or
// gives up. Returns a function that calls the wait off.
export function whenNoSavesWaiting(then) {
  if (waitingSaveCount() === 0) {
    then();
    return () => {};
  }
  let over = false;
  const stop = subscribeWaitingSaves(() => {
    if (over || waitingSaveCount() > 0) return;
    over = true;
    stop();
    then();
  });
  return () => {
    over = true;
    stop();
  };
}

// The browser's "leave this page?" question, up only while a counted save has not landed.
function askBeforeLeaving(e) {
  e.preventDefault();
  // Older browsers need a value; modern ones show their own wording whatever it is.
  e.returnValue = "";
  return "";
}

function keep(save, phase) {
  save.phase = phase;
  if (kept.size === 0 && typeof window !== "undefined") {
    window.addEventListener("beforeunload", askBeforeLeaving);
  }
  kept.add(save);
  publishCounts();
}

function release(save) {
  if (!kept.delete(save)) return;
  if (kept.size === 0 && typeof window !== "undefined") {
    window.removeEventListener("beforeunload", askBeforeLeaving);
  }
  publishCounts();
}

function setPhase(save, phase) {
  if (!kept.has(save) || save.phase === phase) return;
  save.phase = phase;
  publishCounts();
}

// Signing out: every waiting save is let go. Each one's wait ends at once and it fails, marked
// forgotten (a try already out finishes on its own and is marked the same if it fails). The count,
// the bar and the leave question go at once, before the login page or its reload.
export function forgetWaitingSaves() {
  forgetRound += 1;
  const saves = [...kept];
  kept.clear();
  if (typeof window !== "undefined") window.removeEventListener("beforeunload", askBeforeLeaving);
  publishCounts();
  saves.forEach((save) => {
    if (save.stopWaiting) save.stopWaiting();
  });
}

// ---- The wait --------------------------------------------------------------------------------------

// Resolves true once the browser says it is online and our own site answers, asking on "online",
// on every change of the tab's visibility, and every `every` ms. When `deadline` (epoch ms) passes,
// one last answer decides (the question already out, or one more): false unless the site answers.
// save.stopWaiting ends the wait at once with false. Never throws.
function waitForSignal({ deadline, now, canReach, every, save }) {
  return new Promise((resolve) => {
    let over = false;
    let asking = false;
    let askAgain = false;
    let lastChance = false;
    let timer = null;
    let giveUpTimer = null;
    const finish = (answer) => {
      if (over) return;
      over = true;
      save.stopWaiting = null;
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", check);
      clearInterval(timer);
      clearTimeout(giveUpTimer);
      resolve(answer);
    };
    async function check() {
      if (over) return;
      if (browserIsOffline()) {
        if (lastChance) finish(false);
        return;
      }
      // One question at a time; something that happens meanwhile asks once more if that one found no answer.
      if (asking) {
        askAgain = true;
        return;
      }
      asking = true;
      let answered = false;
      try {
        answered = (await canReach()) === true;
      } catch {
        answered = false;
      }
      asking = false;
      if (over) return;
      if (answered && !browserIsOffline()) {
        finish(true);
        return;
      }
      if (lastChance) {
        finish(false);
        return;
      }
      if (askAgain) {
        askAgain = false;
        check();
      }
    }
    save.stopWaiting = () => finish(false);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", check);
    timer = setInterval(check, every);
    const lookOneLastTime = () => {
      lastChance = true;
      // A question already out decides when it answers.
      if (!asking) check();
    };
    const left = deadline - now();
    if (left <= 0) {
      lookOneLastTime();
      return;
    }
    giveUpTimer = setTimeout(lookOneLastTime, left);
    check();
  });
}

// `attempt()` with a clock on it: a try that has neither answered nor failed after `ms` is given up
// on as a connection failure. The try itself is left to finish on its own; a change it saves is
// recognised as done by the next run (see the top of this file).
function attemptWithin(attempt, ms) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      const err = new Error("The save did not answer in time.");
      err.code = ATTEMPT_TIMED_OUT;
      reject(err);
    }, ms);
    Promise.resolve()
      .then(attempt)
      .then(
        (value) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        },
        (err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(err);
        },
      );
  });
}

// Runs `attempt` (which returns a promise) until it answers, waiting for the signal between tries
// that failed for a connection reason. Resolves with the attempt's answer; rejects with the
// attempt's error when it is not a connection error, marked gaveUpWaiting once the wait ran out, or
// marked forgotten when signing out let it go. The options stand in for the clock, the site check
// and the pauses in tests.
export async function saveWhenConnected(
  attempt,
  {
    maxWaitMs = MAX_WAIT_MS,
    attemptTimeoutMs = ATTEMPT_TIMEOUT_MS,
    countAfterMs = COUNT_AFTER_MS,
    now = Date.now,
    canReach = canReachSite,
    every = CHECK_EVERY_MS,
  } = {},
) {
  const deadline = now() + maxWaitMs;
  const round = forgetRound;
  const forgotten = () => forgetRound !== round;
  const save = { phase: null, stopWaiting: null };
  const count = (phase) => {
    if (forgotten()) return;
    if (kept.has(save)) setPhase(save, phase);
    else keep(save, phase);
  };
  let lastTry = false;
  try {
    for (;;) {
      // A try still out after a moment is counted, so a try that hangs is never silent. A try after
      // a wait is counted already: it says it is being tried.
      let countTimer = null;
      if (kept.has(save)) setPhase(save, TRYING_NOW);
      else countTimer = setTimeout(() => count(TRYING_NOW), countAfterMs);
      let answer;
      let failure = null;
      try {
        answer = await attemptWithin(attempt, attemptTimeoutMs);
      } catch (err) {
        failure = err || new Error("The save failed.");
      } finally {
        clearTimeout(countTimer);
      }
      if (!failure) return answer;
      if (forgotten()) throw mark(failure, "forgotten");
      if (!isConnectionError(failure)) throw failure;
      if (lastTry) throw mark(failure, "gaveUpWaiting");
      count(WAITING_FOR_SIGNAL);
      // eslint-disable-next-line no-await-in-loop -- one wait at a time, by design
      const usable = await waitForSignal({ deadline, now, canReach, every, save });
      if (forgotten()) throw mark(failure, "forgotten");
      if (!usable) throw mark(failure, "gaveUpWaiting");
      // Past the half hour, the site answered on the last look: one more try, and that is all.
      if (now() >= deadline) lastTry = true;
    }
  } finally {
    release(save);
  }
}
